"use client";

import { useState, useEffect, useCallback } from "react";
import { cn } from "@/lib/utils";
import { X, ChevronDown, ChevronRight } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useAuth } from "@/contexts/auth-context";
import { getNavigationIcon } from "@/config/modules";
import { useManualUpdates } from "@/hooks/use-manual";

interface NavItem {
  label: string;
  href: string;
  icon: React.ReactNode;
  submenu?: NavItem[];
  badge?: number;
}

interface SidebarDrawerProps {
  open: boolean;
  onClose: () => void;
}

export function SidebarDrawer({ open, onClose }: SidebarDrawerProps) {
  const pathname = usePathname();
  const { menu } = useAuth();
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const { data: manualUpdates } = useManualUpdates();

  const mapNavItems = useCallback((items: typeof menu): NavItem[] => {
    return items.map((item) => {
      const Icon = getNavigationIcon(item.code, item.moduleCode);

      return {
        label: item.label,
        href: item.href,
        icon: <Icon className="size-5" />,
        submenu: item.children ? mapNavItems(item.children) : undefined,
        badge: item.code === "manual" ? manualUpdates?.unread_count : undefined,
      };
    });
  }, [manualUpdates?.unread_count, menu]);

  const navItems = mapNavItems(menu);

  const toggleExpanded = (label: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (open) {
      document.addEventListener("keydown", handleKey);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  // rendering-conditional-render: return null via ternary is fine here,
  // but early return for unmounted state is simpler
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/40 animate-fade-in-overlay"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <nav
        className="absolute inset-y-0 left-0 w-72 bg-background shadow-xl animate-slide-in-left flex flex-col"
        role="navigation"
        aria-label="Menu principal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <span className="text-2xl font-bold tracking-tight">Superbar</span>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-accent active:bg-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
            aria-label="Fechar menu"
          >
            <X className="size-5" />
          </button>
        </div>

        {/* Nav items */}
        <div className="flex-1 min-h-0 py-3 overflow-y-auto">
          {navItems.map((item: NavItem) => {
            const hasSubmenu = item.submenu && item.submenu.length > 0;
            const isExpanded = expandedItems.has(item.label);
            const isBaseDeProducaoGroup = item.href === "/fichas-tecnicas";
            const isActive = pathname === item.href || 
              (item.href !== "/" && pathname.startsWith(item.href)) ||
              (isBaseDeProducaoGroup && pathname.startsWith("/fichas-tecnicas"));
            
            return (
              <div key={`${item.label}-${item.href}`}>
                {hasSubmenu ? (
                  <>
                    <button
                      onClick={() => toggleExpanded(item.label)}
                      className={cn(
                        "w-full flex items-center justify-between gap-3 px-4 py-3.5 text-base font-medium transition-colors rounded-none border-l-2",
                        isActive
                          ? "bg-secondary text-foreground border-foreground"
                          : "text-muted-foreground hover:bg-secondary/50 hover:text-foreground border-transparent"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        {item.icon}
                        {item.label}
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="size-4" />
                      ) : (
                        <ChevronRight className="size-4" />
                      )}
                    </button>
                    {isExpanded && (
                      <div className="bg-secondary/30">
                        {item.submenu!.map((subItem) => {
                          const isSubActive = pathname === subItem.href;
                          return (
                            <Link
                              key={subItem.href}
                              href={subItem.href}
                              onClick={onClose}
                              className={cn(
                                "flex items-center gap-3 pl-12 pr-4 py-2.5 text-base font-medium transition-colors rounded-none border-l-2",
                                isSubActive
                                  ? "bg-secondary text-foreground border-foreground"
                                  : "text-muted-foreground hover:bg-secondary/50 hover:text-foreground border-transparent"
                              )}
                            >
                              {subItem.label}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    href={item.href}
                    onClick={onClose}
                    className={cn(
                      "flex items-center gap-3 px-4 py-3.5 text-base font-medium transition-colors rounded-none border-l-2",
                      isActive
                        ? "bg-secondary text-foreground border-foreground"
                        : "text-muted-foreground hover:bg-secondary/50 hover:text-foreground border-transparent"
                    )}
                  >
                    {item.icon}
                    {item.label}
                    {item.badge !== undefined && item.badge > 0 && (
                      <span className="ml-auto flex size-5 items-center justify-center rounded-full bg-foreground text-background text-xs font-bold">
                        {item.badge > 9 ? "9+" : item.badge}
                      </span>
                    )}
                  </Link>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border">
          <p className="text-sm font-medium text-muted-foreground">Superbar v0.1.0</p>
        </div>
      </nav>
    </div>
  );
}

// Fixed: useDrawer had duplicate 'open' property name
export function useDrawer() {
  const [isOpen, setIsOpen] = useState(false);

  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);
  const close = useCallback(() => setIsOpen(false), []);
  const openDrawer = useCallback(() => setIsOpen(true), []);

  return { isOpen, toggle, close, openDrawer } as const;
}
